// Quoridor 규칙 엔진 (순수 함수, UI 상태 없음)
//
// 보드: 9x9 칸 (row 0~8, col 0~8). player 0(사람)은 row 8에서 시작해 row 0이 목표,
// player 1(AI)은 row 0에서 시작해 row 8이 목표.
// 벽: 8x8 교차점(row/col 0~7)에 가로(h) 또는 세로(v)로 놓임. 한 벽은 두 칸 폭.

export type Player = 0 | 1;
export type Pos = { row: number; col: number };
export type WallOrientation = "h" | "v";

export type Action =
  | { type: "move"; to: Pos }
  | { type: "wall"; orientation: WallOrientation; r: number; c: number };

export interface GameState {
  pawns: [Pos, Pos];
  wallsLeft: [number, number];
  // blockedH.has(`${r},${c}`) → row r와 row r+1 사이, col c에서 세로 이동 차단
  blockedH: Set<string>;
  // blockedV.has(`${r},${c}`) → col c와 col c+1 사이, row r에서 가로 이동 차단
  blockedV: Set<string>;
  // 벽이 이미 사용한 8x8 교차점 (가로/세로 벽이 한 점에서 교차하는 것을 방지)
  usedIntersections: Set<string>;
  turn: Player;
  winner: Player | null;
}

export const BOARD_SIZE = 9;
export const WALLS_PER_PLAYER = 10;

const key = (r: number, c: number) => `${r},${c}`;

export function otherPlayer(p: Player): Player {
  return p === 0 ? 1 : 0;
}

function goalRow(player: Player): number {
  return player === 0 ? 0 : 8;
}

function inBounds(p: Pos): boolean {
  return p.row >= 0 && p.row < BOARD_SIZE && p.col >= 0 && p.col < BOARD_SIZE;
}

export function createInitialState(firstPlayer: Player = 0): GameState {
  return {
    pawns: [
      { row: 8, col: 4 },
      { row: 0, col: 4 },
    ],
    wallsLeft: [WALLS_PER_PLAYER, WALLS_PER_PLAYER],
    blockedH: new Set(),
    blockedV: new Set(),
    usedIntersections: new Set(),
    turn: firstPlayer,
    winner: null,
  };
}

function cloneState(state: GameState): GameState {
  return {
    pawns: [{ ...state.pawns[0] }, { ...state.pawns[1] }],
    wallsLeft: [...state.wallsLeft] as [number, number],
    blockedH: new Set(state.blockedH),
    blockedV: new Set(state.blockedV),
    usedIntersections: new Set(state.usedIntersections),
    turn: state.turn,
    winner: state.winner,
  };
}

// from -> to (한 칸, 상하좌우)가 벽에 막혀있지 않은지 확인. 보드 범위 밖이면 false.
function canStep(state: GameState, from: Pos, to: Pos): boolean {
  if (!inBounds(to)) return false;
  const dr = to.row - from.row;
  const dc = to.col - from.col;
  if (dr !== 0 && dc !== 0) return false;
  if (dr !== 0) {
    if (Math.abs(dr) !== 1) return false;
    const r = Math.min(from.row, to.row);
    return !state.blockedH.has(key(r, from.col));
  }
  if (Math.abs(dc) !== 1) return false;
  const c = Math.min(from.col, to.col);
  return !state.blockedV.has(key(from.row, c));
}

const ORTHO_DIRS: Pos[] = [
  { row: -1, col: 0 },
  { row: 1, col: 0 },
  { row: 0, col: -1 },
  { row: 0, col: 1 },
];

/** player가 이번 턴 이동할 수 있는 목적지 칸 목록 (점프/대각 점프 규칙 포함). */
export function getPawnMoves(state: GameState, player: Player): Pos[] {
  const me = state.pawns[player];
  const opponent = state.pawns[otherPlayer(player)];
  const moves: Pos[] = [];

  for (const d of ORTHO_DIRS) {
    const next = { row: me.row + d.row, col: me.col + d.col };
    if (!canStep(state, me, next)) continue;

    if (next.row === opponent.row && next.col === opponent.col) {
      // 상대 말이 바로 옆에 있으면 직선으로 뛰어넘기를 우선 시도
      const straight = { row: next.row + d.row, col: next.col + d.col };
      if (canStep(state, next, straight)) {
        moves.push(straight);
        continue;
      }
      // 직선 점프가 벽/보드 끝에 막히면 대각선 점프 허용
      const perp: Pos[] =
        d.row !== 0
          ? [
              { row: 0, col: -1 },
              { row: 0, col: 1 },
            ]
          : [
              { row: -1, col: 0 },
              { row: 1, col: 0 },
            ];
      for (const p of perp) {
        const diag = { row: next.row + p.row, col: next.col + p.col };
        if (canStep(state, next, diag)) moves.push(diag);
      }
    } else {
      moves.push(next);
    }
  }
  return moves;
}

/** 벽을 무시하고(상대 말 위치도 무시) player가 목표 행까지 갈 수 있는 최단 거리. 없으면 Infinity. */
export function shortestPathLength(state: GameState, player: Player): number {
  const target = goalRow(player);
  const start = state.pawns[player];
  if (start.row === target) return 0;

  const visited = new Set<string>([key(start.row, start.col)]);
  let frontier: Pos[] = [start];
  let dist = 0;

  while (frontier.length > 0) {
    dist++;
    const next: Pos[] = [];
    for (const p of frontier) {
      for (const d of ORTHO_DIRS) {
        const np = { row: p.row + d.row, col: p.col + d.col };
        if (!canStep(state, p, np)) continue;
        const k = key(np.row, np.col);
        if (visited.has(k)) continue;
        visited.add(k);
        if (np.row === target) return dist;
        next.push(np);
      }
    }
    frontier = next;
  }
  return Infinity;
}

function placeWallEdges(
  state: GameState,
  orientation: WallOrientation,
  r: number,
  c: number,
): GameState {
  const next = cloneState(state);
  next.usedIntersections.add(key(r, c));
  if (orientation === "h") {
    next.blockedH.add(key(r, c));
    next.blockedH.add(key(r, c + 1));
  } else {
    next.blockedV.add(key(r, c));
    next.blockedV.add(key(r + 1, c));
  }
  return next;
}

export function canPlaceWall(
  state: GameState,
  orientation: WallOrientation,
  r: number,
  c: number,
  player: Player,
): boolean {
  if (state.winner !== null) return false;
  if (state.wallsLeft[player] <= 0) return false;
  if (r < 0 || r > 7 || c < 0 || c > 7) return false;
  if (state.usedIntersections.has(key(r, c))) return false;

  if (orientation === "h") {
    if (state.blockedH.has(key(r, c)) || state.blockedH.has(key(r, c + 1))) {
      return false;
    }
  } else {
    if (state.blockedV.has(key(r, c)) || state.blockedV.has(key(r + 1, c))) {
      return false;
    }
  }

  // 양쪽 모두에게 목표까지 최소 한 경로는 남아 있어야 한다.
  const tentative = placeWallEdges(state, orientation, r, c);
  return (
    shortestPathLength(tentative, 0) < Infinity &&
    shortestPathLength(tentative, 1) < Infinity
  );
}

export function listLegalWalls(
  state: GameState,
  player: Player,
): { orientation: WallOrientation; r: number; c: number }[] {
  if (state.wallsLeft[player] <= 0) return [];
  const result: { orientation: WallOrientation; r: number; c: number }[] = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (canPlaceWall(state, "h", r, c, player)) {
        result.push({ orientation: "h", r, c });
      }
      if (canPlaceWall(state, "v", r, c, player)) {
        result.push({ orientation: "v", r, c });
      }
    }
  }
  return result;
}

export function applyAction(state: GameState, action: Action): GameState {
  const player = state.turn;
  let next: GameState;

  if (action.type === "move") {
    next = cloneState(state);
    next.pawns[player] = { ...action.to };
  } else {
    next = placeWallEdges(state, action.orientation, action.r, action.c);
    next.wallsLeft[player] -= 1;
  }

  if (next.pawns[player].row === goalRow(player)) {
    next.winner = player;
  }
  next.turn = otherPlayer(player);
  return next;
}
